import {
    OptionsProductCustomizationResponseDto,
    ProductCustomizationResponseDto,
} from 'client/widget-components/ecom/types/EcomSDK';

/**
 * React port of snipcart-client's product-customization model + mappers.
 *
 * Mirrors:
 *  - `sdk/core/models/cart/CartItem.ts`            (the `ProductCustomization` model)
 *  - `sdk/core/mappers/ProductCustomizationMapper` (`FromResponseDto` / `ToDisplayValue`)
 *  - `sdk/core/mappers/StringMapper`               (list/file/bytes/mimetype helpers)
 *
 * Kept as plain functions (no class) to match the rest of this widget.
 */

/** A function that renders a raw amount as a localized currency string. */
export type FormatMoney = (amount: number) => string;

/* -------------------------------------------------------------------------- */
/*  Model (mirrors CartItem.ts `ProductCustomization`)                        */
/* -------------------------------------------------------------------------- */

export interface ProductCustomizationFileAttachment {
    filename: string;
    mimetype: string;
    url: string;
    size: number;
    key: string;
    handle: string;
    uploadedAt: string;
}

export interface TextProductCustomization {
    id: string;
    label: string;
    type: 'TEXT';
    value?: string;
    priceModifier?: number;
}

export interface BooleanProductCustomization {
    id: string;
    label: string;
    type: 'BOOLEAN';
    value: boolean;
    priceModifier?: number;
}

export interface OptionsProductCustomization {
    id: string;
    label?: string | null;
    type: 'OPTIONS';
    value: string[];
    optionsPriceModifiers?: Array<number | null>;
    priceModifier?: number;
}

export interface FileProductCustomization {
    id: string;
    label: string;
    type: 'FILE';
    value: ProductCustomizationFileAttachment[];
    priceModifier?: number;
}

export type ProductCustomization =
    | TextProductCustomization
    | BooleanProductCustomization
    | OptionsProductCustomization
    | FileProductCustomization;

/**
 * One distinct option the shopper picked, with the repeats collapsed into a
 * quantity and their price modifiers totalled.
 */
export interface OptionSelection {
    value: string;
    quantity: number;
    /** Total for every repeat of this option, or `null` when none of them is priced. */
    priceModifier: number | null;
}

/* -------------------------------------------------------------------------- */
/*  StringMapper helpers                                                      */
/* -------------------------------------------------------------------------- */

/** Vue's `Intl.ListFormat({ type: 'conjunction', style: 'narrow' })` join. */
export function joinList(input: string[], locale?: string): string {
    if (typeof Intl?.ListFormat !== 'function') {
        return input.join(', ');
    }
    try {
        return new Intl.ListFormat(locale, {
            type: 'conjunction',
            style: 'narrow',
        }).format(input);
    } catch {
        return input.join(', ');
    }
}

/**
 * Human-readable file size (e.g. `1.2 MB`). 1024-based tiers, locale-aware
 * digits. Mirrors `StringMapper.FromBytes`.
 */
export function formatBytes(bytes: number, locale?: string): string {
    if (!Number.isFinite(bytes) || bytes < 0) {
        return '';
    }
    const k = 1024;
    const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
    if (bytes === 0) {
        return `0 ${units[0]}`;
    }
    const i = Math.min(
        Math.floor(Math.log(bytes) / Math.log(k)),
        units.length - 1
    );
    if (i <= 0) {
        return `${bytes.toLocaleString(locale)} ${units[0]}`;
    }
    const n = bytes / k ** i;
    const fractionDigits = n >= 10 || n === Math.floor(n) ? 0 : 1;
    return `${n.toLocaleString(locale, {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: 0,
    })} ${units[i]}`;
}

/** Label for a cart file attachment: `name.ext` or `name.ext (1.2 MB)`. */
export function fileLabel(
    file: Pick<ProductCustomizationFileAttachment, 'filename' | 'size'>,
    locale?: string
): string {
    const sizeLabel =
        typeof file.size === 'number' && file.size >= 0
            ? formatBytes(file.size, locale)
            : '';
    return sizeLabel ? `${file.filename} (${sizeLabel})` : file.filename;
}

/**
 * Maps an attachment MIME type to a file-format icon name (`file-format-*`).
 * Unknown or empty types use `file-format-generic`. Mirrors
 * `StringMapper.FromMimetype_ToIconName`.
 */
export function iconNameFromMimetype(mimetype: string): string {
    const type = mimetype.split(';')[0].trim().toLowerCase();
    if (!type) {
        return 'file-format-generic';
    }

    switch (type) {
        case 'application/pdf':
            return 'file-format-pdf';
        case 'image/png':
            return 'file-format-png';
        case 'image/jpeg':
        case 'image/jpg':
            return 'file-format-jpg';
        case 'image/svg+xml':
            return 'file-format-svg';
        case 'text/csv':
        case 'application/csv':
            return 'file-format-csv';
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return 'file-format-excel';
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return 'file-format-word';
        default:
            break;
    }

    if (type.startsWith('audio/')) {
        return 'file-format-audio';
    }
    if (type.startsWith('video/')) {
        return 'file-format-video';
    }

    return 'file-format-generic';
}

/* -------------------------------------------------------------------------- */
/*  ProductCustomizationMapper                                                */
/* -------------------------------------------------------------------------- */

/**
 * Maps an API customization DTO to the display model, or `null` when there is
 * no value to show (mirrors `ProductCustomizationMapper.FromResponseDto`).
 */
export function fromResponseDto(
    input: ProductCustomizationResponseDto
): ProductCustomization | null {
    if (
        input.value === null ||
        input.value === undefined ||
        (Array.isArray(input.value) && input.value.length === 0)
    ) {
        return null;
    }

    switch (input.type) {
        case 'FILE':
            return {
                id: input.id,
                type: 'FILE',
                label: input.label,
                value: input.value.map((f) => ({
                    filename: f.filename,
                    mimetype: f.mimetype,
                    url: f.url,
                    size: f.size,
                    key: f.key,
                    handle: f.handle,
                    uploadedAt: f.uploaded_at,
                })),
                priceModifier: input.price_modifier ?? undefined,
            };
        case 'OPTIONS': {
            const optionsPriceModifiers = (
                input as OptionsProductCustomizationResponseDto
            ).options_price_modifiers;
            return {
                id: input.id,
                type: 'OPTIONS',
                label: input.label,
                value: input.value,
                optionsPriceModifiers: optionsPriceModifiers ?? undefined,
                // Total of the options price modifiers if they exist.
                priceModifier:
                    (optionsPriceModifiers?.length ?? 0) > 0
                        ? optionsPriceModifiers?.reduce<number>(
                              (sum, mod) => sum + (mod ?? 0),
                              0
                          )
                        : undefined,
            };
        }
        case 'BOOLEAN':
            return {
                id: input.id,
                type: 'BOOLEAN',
                label: input.label,
                value: input.value,
                priceModifier: input.price_modifier ?? undefined,
            };
        case 'TEXT':
        default:
            return {
                id: input.id,
                type: 'TEXT',
                label: input.label,
                value: input.value,
                priceModifier: input.price_modifier ?? undefined,
            };
    }
}

/**
 * Collapses an OPTIONS value into one entry per distinct option.
 *
 * Per-option quantity is expressed as a repeated value: picking `Red` twice
 * arrives as `value: ['Red', 'Red']` with one positional entry per repeat in
 * `optionsPriceModifiers`. Without collapsing, the summary lists the same option
 * once per unit instead of showing it once with its quantity. Mirrors
 * `ProductCustomizationMapper.ToOptionSelections`.
 */
export function toOptionSelections(
    customization: OptionsProductCustomization
): OptionSelection[] {
    const selections = new Map<string, OptionSelection>();

    customization.value.forEach((value, index) => {
        const modifier = customization.optionsPriceModifiers?.[index] ?? null;
        const selection = selections.get(value);

        if (!selection) {
            selections.set(value, { value, quantity: 1, priceModifier: modifier });
            return;
        }

        selection.quantity += 1;
        if (modifier !== null) {
            selection.priceModifier = (selection.priceModifier ?? 0) + modifier;
        }
    });

    return [...selections.values()];
}

/**
 * The plain-text value rendered inline for a customization, or `null` when the
 * value is shown some other way (FILE chips) or there is nothing to show
 * (BOOLEAN). Mirrors `ProductCustomizationMapper.ToDisplayValue`.
 */
export function toDisplayValue(
    customization: ProductCustomization,
    formatMoney: FormatMoney,
    locale?: string
): string | null {
    switch (customization.type) {
        case 'TEXT':
            return customization.value ?? null;
        case 'OPTIONS': {
            // One part per distinct option: `Red`, `Red ×2`, `Red ×2 ($20.00)`.
            const parts = toOptionSelections(customization).map(
                ({ value, quantity, priceModifier }) => {
                    const label = quantity > 1 ? `${value} ×${quantity}` : value;
                    return priceModifier != null
                        ? `${label} (${formatMoney(priceModifier)})`
                        : label;
                }
            );
            return joinList(parts, locale);
        }
        case 'FILE': {
            // Custom markup is used for file customizations; this is a fallback.
            const parts = customization.value.map((file) =>
                fileLabel(file, locale)
            );
            return joinList(parts, locale);
        }
        case 'BOOLEAN':
        default:
            // No value to display for boolean customizations.
            return null;
    }
}
